import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ChessGame from "./ChessGame";
import { useAuth } from "../context/AuthContext";
import { API_CONFIG } from "../config/api";
import { generateAuthToken, generateURN } from "../components/hmac";
import "../styles/coursePage.css";

const ModulePage = () => {
  const { level, moduleId } = useParams();
  const navigate = useNavigate();

  const { user, profileUser, setProfileUser } = useAuth();

  const chessRef = useRef(null);

  const [module, setModule] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [expandedTopicId, setExpandedTopicId] = useState(null);

  const [puzzleList, setPuzzleList] = useState([]);
  const [selectedPuzzle, setSelectedPuzzle] = useState(null);

  const solvedPuzzleIds = new Set(profileUser?.puzzleSolved || []);

  /* =========================
     RESPONSIVE STATE
  ========================= */
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isCompactView = screenWidth <= 1020;

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* =========================
     MOBILE MODAL
  ========================= */
  const [showTopicMenu, setShowTopicMenu] = useState(false);

  /* =========================
     SCROLL TO CHESS
  ========================= */
 const scrollToChess = useCallback(() => {
  if (isCompactView) {
    chessRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
}, [isCompactView]);


  /* =========================
     LOAD MODULE
  ========================= */
const [courseData, setCourseData] = useState([]);

useEffect(() => {
  const stored = sessionStorage.getItem("COURSE_DATA");
  if (stored) {
    setCourseData(JSON.parse(stored));
  }
}, []);

useEffect(() => {
  if (!courseData.length) return;

  const levelObj = courseData.find(
    (l) => l.level.toLowerCase() === level
  );

  if (!levelObj) {
    console.error("Level not found");
    return;
  }

  const mod = levelObj.modules.find(
    (m) => String(m.moduleId) === String(moduleId)
  );

  if (!mod) {
    console.error("Module not found");
    return;
  }

  setModule(mod);
  setSelectedTopic(mod.topics?.[0] || null);
  setExpandedTopicId(mod.topics?.[0]?.topicId || null);
}, [level, moduleId, courseData]);



  /* =========================
     LOAD PUZZLES
  ========================= */
    const fetchPuzzles = useCallback(async (topicId) => {
    try {
      setPuzzleList([]);
      setSelectedPuzzle(null);

      const { host, fetchPuzzlesByTopic, basic } = API_CONFIG;
      const urn = generateURN();
      const authToken = generateAuthToken(urn);

      const res = await fetch(
        `https://${host}:${fetchPuzzlesByTopic.path}`,
        {
          method: fetchPuzzlesByTopic.method,
          headers: {
            "Content-Type": "application/json",
            topicId,
            Authorization: `Basic ${basic.BASIC_AUTH_CWUSER}`,
            urn,
            authToken,
          },
          credentials: "include",
        }
      );

      const json = await res.json();
      const puzzles = json?.apiResponseData?.responseData || [];

      setPuzzleList(puzzles);
      setSelectedPuzzle(puzzles[0]);
      scrollToChess();
    } catch (err) {
      console.error("Puzzle fetch failed", err);
    }
  }, [scrollToChess]);

  useEffect(() => {
  if (!selectedTopic?.topicId) return;
  fetchPuzzles(selectedTopic.topicId);
  }, [selectedTopic?.topicId, fetchPuzzles]);

  /* =========================
     NEXT PUZZLE
  ========================= */
  const handleNextPuzzle = () => {
    if (!selectedPuzzle || puzzleList.length === 0) return;

    const currentIndex = puzzleList.findIndex(
      (p) => p.puzzleId === selectedPuzzle.puzzleId
    );

    if (currentIndex === -1) return;

    const nextPuzzle = puzzleList[currentIndex + 1];

    if (nextPuzzle) {
      setSelectedPuzzle(nextPuzzle);
      scrollToChess();
    }
  };

  /* =========================
     PUZZLE SOLVED (BACKEND ONLY)
  ========================= */
  const handlePuzzleSolved = async (puzzleId) => {
  if (!user?.userEmail || !profileUser) return;

  // ✅ INSTANT UI UPDATE
  setProfileUser((prev) => {
    if (!prev) return prev;
    if (prev.puzzleSolved?.includes(puzzleId)) return prev;

    return {
      ...prev,
      puzzleSolved: [...(prev.puzzleSolved || []), puzzleId],
    };
  });

  // 🔁 BACKEND SYNC
  try {
    const { host, solvePuzzle, basic,fetchUserInfo } = API_CONFIG;
    const urn = generateURN();
    const authToken = generateAuthToken(urn);

    await fetch(`https://${host}${solvePuzzle.path}`, {
      method: solvePuzzle.method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${basic.BASIC_AUTH_CWUSER}`,
        urn,
        authToken,
      },
      credentials: "include",
      body: JSON.stringify({
        userEmail: user.userEmail,
        puzzleId,
      }),
    });

    const res = await fetch(
      `https://${host}${fetchUserInfo.path}`,
      {
        method: fetchUserInfo.method,
        headers: {
          "Content-Type": "application/json",
          useremail: user.userEmail,
          Authorization: `Basic ${basic.BASIC_AUTH_CWUSER}`,
          urn,
          authToken,
        },
          credentials: "include",
      }
    );

    const data = await res.json();
    const userData = data?.apiResponseData?.responseData;

    if (userData) {
      setProfileUser(userData); 
    }

  } catch (err) {
    console.error("Solve API failed", err);
  }
};


  /* =========================
     VIDEO
  ========================= */
  const renderVideo = (url) => {
    if (!url) return null;

    if (url.includes("youtube")) {
      const videoId = url.includes("shorts/")
        ? url.split("shorts/")[1].split("?")[0]
        : url.split("v=")[1]?.split("&")[0];

      return (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title="YouTube video"
          frameBorder="0"
          allowFullScreen
        />
      );
    }

    return (
      <video controls muted playsInline>
        <source src={url} type="video/mp4" />
      </video>
    );
  };

 if (!module) return null;

  return (
    <div className="module-page">
      {!isCompactView && (
        <div className="topic-list">
          {module.topics.map((topic) => {
            const isExpanded = expandedTopicId === topic.topicId;

            return (
              <div key={topic.topicId}>
                <div
                  className={`topic-item ${
                    selectedTopic?.topicId === topic.topicId
                      ? "active"
                      : ""
                  }`}
                  onClick={() => {
                    setSelectedTopic(topic);
                    setExpandedTopicId(isExpanded ? null : topic.topicId);
                  }}
                >
                  {topic.topic}
                </div>

                {isExpanded &&
                  puzzleList.map((puzzle, index) => (
                    <div
                      key={puzzle.puzzleId}
                      className={`puzzle-title ${
                        selectedPuzzle?.puzzleId === puzzle.puzzleId
                          ? "active"
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedPuzzle(puzzle);
                        scrollToChess();
                      }}
                    >
                      Puzzle {index + 1}
                      {solvedPuzzleIds.has(puzzle.puzzleTopicId) && (
                        <span className="puzzle-tick"> ✔</span>
                      )}
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      )}

      {/* RIGHT CONTENT */}
      <div className="module-content">
        <div className="module-back">
          <div className="back" onClick={() => navigate("/courses")}>
            <h3>← Back</h3>
          </div>
        </div>

        <div
          className={`topic-bar ${isCompactView ? "clickable" : ""}`}
          onClick={() => isCompactView && setShowTopicMenu(true)}
        >
          <span className="topic-bar-title">
            {selectedTopic?.topic || "Select Topic"}
          </span>
          {isCompactView && <span className="topic-bar-icon">▾</span>}
        </div>

        <div className="module-main-grid">
         <div className="chess-area" ref={chessRef}>
            {selectedPuzzle && (
              <ChessGame
                key={selectedPuzzle.puzzleId}
                fen={selectedPuzzle.fen}
                puzzleId={selectedPuzzle.puzzleTopicId}
                explaination={selectedPuzzle.explaination} 
                solution={selectedPuzzle.solution}
                onSolved={handlePuzzleSolved}
                onNextPuzzle={handleNextPuzzle}
              />
            )}
          </div>

          <div className="video-side">
            {renderVideo(selectedTopic?.videoUrl)}
          </div>
        </div>
      </div>

      {/* MOBILE MODAL */}
      {showTopicMenu && isCompactView && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowTopicMenu(false)}
        >
          <div
            className="mobile-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Select Topic</h3>

            {module.topics.map((topic) => {
              const isExpanded = expandedTopicId === topic.topicId;

              return (
                <div key={topic.topicId}>
                  <div
                    className={`topic-item ${
                      selectedTopic?.topicId === topic.topicId
                        ? "active"
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedTopic(topic);
                      setExpandedTopicId(isExpanded ? null : topic.topicId);
                      setShowTopicMenu(false);
                    }}
                  >
                    {topic.topic}
                  </div>

                  {isExpanded &&
                    puzzleList.map((puzzle, index) => (
                      <div
                        key={puzzle.puzzleId}
                        className="puzzle-title"
                        onClick={() => {
                          setSelectedPuzzle(puzzle);
                          setShowTopicMenu(false);
                          scrollToChess();
                        }}
                      >
                        Puzzle {index + 1}
                        {solvedPuzzleIds.has(puzzle.puzzleTopicId) && (
                          <span className="puzzle-tick"> ✔</span>
                        )}
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModulePage;
